import { PERMISSIONS } from '$app/constants/common'
import TenantEmpty from '$blocks/TenantEmpty'
import Loading from '$components/Loading'
import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { usePermissions } from '$hooks/actions'
import { useAppScopeService, useCountryService } from '$hooks/services'
import { updateCurrentTenant, useTenantStore } from '$hooks/services/useTenants'
import { useCountryStore } from '$hooks/stores/useCountryStore'
import { useWebhookEventStore } from '$hooks/stores/useWebhookEventStore'
import { getWebhookEventsByCategory } from '$services/api'
import { useRequest } from 'ahooks'
import { FC, PropsWithChildren, useEffect } from 'react'

const TenantGuard: FC<PropsWithChildren> = ({ children }) => {
  const { route } = useRouteSummary()
  const { tenantId = '' } = route.params

  const currentTenantId = useTenantStore(state => state.currentTenant?.id)
  const tenants = useTenantStore(state => state.tenants)

  const updateWebhookEvents = useWebhookEventStore(
    state => state.updateWebhookEvents
  )
  const { run: fetchWebhookEventsByCategory } = useRequest(
    getWebhookEventsByCategory,
    {
      manual: true,
      onSuccess: ({ data }) => {
        updateWebhookEvents(data)
      }
    }
  )

  const { fetchAppScope, fetchingAppScope } = useAppScopeService()

  const { fetchCountries } = useCountryService()

  const { checkPermissions } = usePermissions()
  const CAN_VIEW_COUNTRIES = checkPermissions([PERMISSIONS.COUNTRY_VIEW])

  const updateCountries = useCountryStore(state => state.updateCountries)

  useEffect(() => {
    if (!tenantId) return

    fetchAppScope()
    fetchWebhookEventsByCategory()

    if (!CAN_VIEW_COUNTRIES) {
      updateCountries([])
      return
    }

    fetchCountries()
  }, [
    tenantId,
    CAN_VIEW_COUNTRIES,
    fetchWebhookEventsByCategory,
    fetchAppScope,
    fetchCountries,
    updateCountries
  ])

  useEffect(() => {
    if (!tenants.length) return

    if (tenantId) {
      updateCurrentTenant(tenantId)
      return
    }

    if (currentTenantId) return

    updateCurrentTenant(tenants[0].id)
  }, [currentTenantId, tenantId, tenants])

  const isAnyLoading = [fetchingAppScope].some(loading => loading)

  const ready = !!currentTenantId && !!tenantId && !isAnyLoading
  const isEmptyTenants = ready && !tenants.length

  if (!ready) return <Loading />

  if (isEmptyTenants) return <TenantEmpty />

  return <>{children}</>
}

export default TenantGuard
